import { IFileState, systemRights } from "@tedivo/tvd-api-models";
import { ITedivoShoelaceSelectOptions, createSelectShoelace } from "@tedivo/tedivo-form";
import {
  TDownloadType,
  createStafFileBlobWithDialog,
} from "./edits/helpers/export/createStafFileBlobWithDialog";

import Services from "../../../../app/services";
import { ViewJsonTypeEnum } from "../ViewJsonTypeEnum";
import { applyCgsOptionToOvd } from "./edits/helpers/export/cgsExportOptions";
import { cloneFileDialog } from "../../my-cloud/cloneFileDialog";
import { createNsdExportOptionsDialog } from "./edits/helpers/export/createNsdExportOptionsDialog";
import { createSimpleAlert } from "@tedivo/tedivo-ui";
import { getTranslation } from "../../../../app/i18/i18tn";
import { nsdBase64ToBlob } from "../../../../app/services/ovd-nsd-converter/ovdNsdConverter";
import ovdJsonStore from "../../../../app/stores/OVDJsonStore";
import { routeFns } from "../../../../app/router/routes";
import router from "../../../../app/router";
import securityModule from "../../../../app/security/SecurityModule";
import stafSaveOptions from "../../../../app/stores/stafSaveOptions";
import { startDownloadInBrowser } from "@tedivo/tedivo-dom-helpers";

export function createDownloadOVDJsonLink(
  viewType: ViewJsonTypeEnum,
  fileOrgId: string,
  fileId: string,
  currentFileState: IFileState,
): HTMLElement | null {
  const {
    currentJson: json,
    filenameSanitized: name,
    filenameSanitizedForSE,
    lcgVcgTcgAndPairings,
  } = ovdJsonStore;

  if (securityModule.planIsReadOnly) {
    return null;
  }

  const userIsTedivoAdmin = securityModule.userIsTedivoAdmin;

  if (currentFileState === "DRAFT" && !userIsTedivoAdmin) {
    return createNoDownloadButton();
  }

  const cgsStats = lcgVcgTcgAndPairings?.cgStats;

  const options: TDownloadOptions = [
    // A. OVD JSON ---------------------------------------------------
    canDownloadGate(systemRights.ORG.DownloadOwnOrgFileAsOvd, fileOrgId)
      ? {
          name: getTranslation("view:download.downloadOVDJson"),
          value: "ovdJson",
          icon: "download",
        }
      : undefined,
    // B. STAF --------------------------------------------------------
    canDownloadGate(systemRights.ORG.DownloadOwnOrgFileAsStaf, fileOrgId)
      ? {
          name: getTranslation("view:download.downloadSTAF"),
          value: "stafFile",
          icon: "download",
        }
      : undefined,
    // C. NSD --------------------------------------------------------
    canDownloadGate(
      systemRights.ORG.DownloadOwnOrgFileAsNsd,
      fileOrgId,
      () => securityModule.currentOrganizationOvdToNsdAllowed,
    )
      ? {
          name: getTranslation("view:download.downloadNSD"),
          value: "nsdFile",
          icon: "download",
        }
      : undefined,
    // D. Clone to my Account  ----------------------------------------
    viewType === ViewJsonTypeEnum.VIEW_OVD &&
    (securityModule.userHasPermission(systemRights.ORG.CanDownloadPublicFile) ||
      userIsTedivoAdmin) &&
    currentFileState === "COMMUNITY"
      ? {
          name: getTranslation("general:search.cloneItToMyAccount"),
          value: "cloneToMyAccount",
          icon: "cloud-download-fill",
        }
      : undefined,
  ].filter(Boolean) as TDownloadOptions;

  if (options.length === 0) {
    return document.createElement("span");
  }

  const modal = document.createElement("sl-dialog");

  // The NSD conversion happens server-side, so that one option needs a blocker.
  const loadingBlocker = document.createElement("loading-blocker-element");

  const downloadSelect = createSelectShoelace({
    id: "downloadBtn",
    caret: false,
    pill: true,
    ommitCheckSign: true,
    size: "small",
    variant: "primary",
    buttonText: getTranslation("view:download.title"),
    iconPrefix: "download",
    options,
    onChange: async (v: TDownloadType) => {
      if (!json) return;

      switch (v) {
        case "ovdJson":
          // Directly download the OVD JSON
          downloadBlobInBrowser(
            new Blob([JSON.stringify(json, null, stafSaveOptions.isFormattedJsonSaved ? 2 : 0)], {
              type: "application/json",
            }),
            `${name}.json`,
          );

          break;

        case "stafFile":
          // Open the modal to download the STAF file
          downloadBlobInBrowser(
            await createStafFileBlobWithDialog(json, cgsStats),
            `${filenameSanitizedForSE}.txt`,
          );

          break;

        case "nsdFile":
          {
            // Open the modal to configure the NSD export
            const nsdOptions = await createNsdExportOptionsDialog();

            if (!nsdOptions) return;

            // Unlike OVD/STAF, the NSD is built by the converter Lambda, so this
            // is a network round-trip and the UI is blocked while it runs.
            loadingBlocker.show();

            try {
              // The CGs choice changes the OVD that is sent to the converter,
              // not the NSD itself, so it is applied before the round-trip.
              const jsonToConvert = applyCgsOptionToOvd(json, nsdOptions.addRemoveCgs);

              const res = await Services.ovdNsdConverter.ovdToNsd(jsonToConvert, {
                heightFactor: nsdOptions.heightFactor,
              });

              // HttpClient already toasts on a non-2xx, so an empty payload just stops here.
              if (!res.data) return;

              downloadBlobInBrowser(nsdBase64ToBlob(res.data.nsd), `${filenameSanitizedForSE}.nsd`);

              // Residuals are NOT errors - a good conversion normally returns
              // some - but the user should see what could not be reproduced.
              if (res.data.residuals.length && nsdOptions.showDebug)
                showNsdResidualsAlert(res.data.residuals);
            } finally {
              loadingBlocker.hide();
            }
          }

          break;

        case "cloneToMyAccount":
          cloneFileDialog({
            onlyCommunity: true,
            actionLabel: "general:search.cloneItToMyAccount",
            goSquaredLabel: "TVL - Clone file",
            modal: modal,
            fileData: {
              name: json?.shipData.shipName || "",
              fileId: fileId,
              organizationId: fileOrgId,
            },
          });
          break;
        case "compare":
          {
            const version = router.getRouteParams().version;
            if (!version) return;
            const route = routeFns.fileCompareOwnVersion(fileId, version);
            router.navigate(route, { organizationId: fileOrgId });
          }
          break;
      }
    },
  });

  const btnHolder = document.createElement("div");
  btnHolder.appendChild(downloadSelect);
  btnHolder.appendChild(modal);
  btnHolder.appendChild(loadingBlocker);

  return btnHolder;

  function canDownloadGate(right: string, orgId: string, extra?: () => boolean) {
    if (userIsTedivoAdmin) return true;

    return (
      currentFileState !== "DRAFT" &&
      securityModule.userHasPermission(right) &&
      orgId === securityModule.currentOrganizationId &&
      (!extra || extra())
    );
  }
}

/** Starts a download and releases the object URL afterwards, so the Blob is not
 *  held in memory for the whole life of the document. */
function downloadBlobInBrowser(blob: Blob | undefined, fileName: string) {
  if (!blob) return;

  const url = URL.createObjectURL(blob);

  startDownloadInBrowser(url, fileName);

  // Revoking synchronously can cancel the download in some browsers, so it waits
  // until the click that started it has been processed.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Lists what the converter could not reproduce exactly in the .nsd. */
function showNsdResidualsAlert(residuals: string[]) {
  const content = document.createElement("div");

  const message = document.createElement("p");
  message.innerHTML = getTranslation("view:download.nsdResiduals");

  const list = document.createElement("ul");
  residuals.forEach((residual) => {
    const li = document.createElement("li");
    li.textContent = residual;
    list.appendChild(li);
  });

  content.appendChild(message);
  content.appendChild(list);

  createSimpleAlert({
    title: getTranslation("view:download.downloadNSD"),
    message: content,
    closeLabel: getTranslation("general:common.ok"),
  });
}

function createNoDownloadButton() {
  const btn = document.createElement("sl-button");
  btn.size = "small";
  btn.outline = true;
  btn.pill = true;

  const icon = document.createElement("sl-icon");
  icon.name = "download";
  icon.slot = "prefix";

  btn.variant = "neutral";
  btn.innerHTML = getTranslation("view:download.title");
  btn.appendChild(icon);

  btn.addEventListener("click", () => {
    createSimpleAlert({
      title: getTranslation("view:download.downloadNotPossible"),
      message: getTranslation("view:download.downloadNotPossibleText"),
      onOk: () => null,
      closeLabel: getTranslation("general:common.ok"),
    });
  });

  return btn;
}

type TDownloadOptions = Array<ITedivoShoelaceSelectOptions & { value: TDownloadType }>;
